import React, { useState, useEffect, useCallback } from 'react';
import { playCorrect, playWrong, playClap } from '../../utils/sounds';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY    = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR      = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT       = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;
const flipCard = keyframes`
  0%   { transform: rotateY(0deg); }
  100% { transform: rotateY(180deg); }
`;
const matchPop = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
const IMG_VERSION = 'v2';
const EMOJI_TO_SLUG: Record<string, string> = {
  '🐘':'elephant','🦁':'lion','🐱':'cat','🐶':'dog','🐔':'chicken',
  '🐄':'cow','🐐':'goat','🦆':'duck','🐟':'fish','🐬':'dolphin',
  '🐋':'whale','🐢':'turtle','🐵':'monkey','🐯':'tiger','🐻':'bear',
  '🐼':'panda','🐨':'koala','🦒':'giraffe','🦓':'zebra','🦏':'rhino',
  '🐴':'horse','🐷':'pig','🐦':'bird','🦅':'eagle','🐧':'penguin',
  '🐍':'snake','🐊':'crocodile','🐸':'frog','🐰':'rabbit','🐭':'mouse',
  '🐹':'hamster','🦊':'fox','🦩':'flamingo','🐝':'bee','🐫':'camel','🦌':'deer',
};

interface MemItem { symbol: string; name: string; }
interface Card { id: number; symbol: string; isFlipped: boolean; isMatched: boolean; }
type GameMode = 'animals' | 'alphabets';

const ANIMALS: MemItem[] = [
  { symbol:'🐶',name:'Anjing' },{ symbol:'🐱',name:'Kucing' },
  { symbol:'🐭',name:'Tikus' },{ symbol:'🐹',name:'Hamster' },
  { symbol:'🐰',name:'Kelinci' },{ symbol:'🦊',name:'Rubah' },
  { symbol:'🐻',name:'Beruang' },{ symbol:'🐼',name:'Panda' },
];
const ALPHABETS: MemItem[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(l => ({ symbol: l, name: l.toLowerCase() }));

// ─── Intro ────────────────────────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR}; display: flex; flex-direction: column;
  align-items: center;
  overflow: hidden; font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatCard = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 50px; height: 50px; border-radius: 12px;
  background: #fff; border: 3px solid rgba(45,188,175,0.3);
  display: flex; align-items: center; justify-content: center;
  font-size: 24px; box-shadow: 0 4px 14px rgba(0,0,0,0.1);
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
`;
const CharCard = styled.div`
  font-size: clamp(72px, 20vw, 110px);
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`width: 40px; height: 4px; background: #e0e0e0; border-radius: 99px; margin: 0 auto 18px;`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900; margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 16px;`;
const ModeRow = styled.div`display: flex; gap: 10px; margin-bottom: 16px;`;
const ModeBtn = styled.button<{ $active: boolean }>`
  flex: 1; padding: 10px 14px; border-radius: 14px;
  border: 2px solid ${p => p.$active ? PRIMARY : '#e0e0e0'};
  background: ${p => p.$active ? 'rgba(45,188,175,0.08)' : '#fff'};
  font-size: 14px; font-weight: 700; color: ${p => p.$active ? PRIMARY : '#7a8a8a'};
  cursor: pointer; transition: all 0.2s; font-family: ${FONT};
  &:hover { border-color: ${PRIMARY}; }
`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4); font-family: ${FONT};
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

// ─── Game ─────────────────────────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const MovesBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px; font-size: 14px; font-weight: 800;
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; padding: 16px; gap: 16px;
  max-width: 440px; margin: 0 auto; width: 100%;
`;
const CardGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 8px; width: 100%;
`;
const MemCard = styled.button<{ $flipped: boolean; $matched: boolean }>`
  aspect-ratio: 1; border-radius: 14px; border: 3px solid;
  border-color: ${p => p.$matched ? '#22c55e' : p.$flipped ? PRIMARY : '#e2e8f0'};
  background: ${p => p.$matched ? 'rgba(34,197,94,0.1)' : p.$flipped ? 'rgba(45,188,175,0.08)' : 'linear-gradient(135deg, #f8faff, #eef2ff)'};
  cursor: ${p => p.$matched ? 'default' : 'pointer'};
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  animation: ${p => p.$matched ? css`${matchPop} 0.4s ease` : 'none'};
  &:hover:not(:disabled) { transform: ${p => !p.$flipped && !p.$matched ? 'translateY(-2px)' : 'none'}; }
  &:disabled { cursor: default; }
`;
const CardFace = styled.div<{ $isQuestion: boolean }>`
  font-size: ${p => p.$isQuestion ? 'clamp(20px, 6vw, 28px)' : 'clamp(16px, 5vw, 24px)'};
  font-weight: 900; color: ${p => p.$isQuestion ? '#cbd5e1' : '#1a2a2a'};
`;
const NewGameBtn = styled.button`
  padding: 12px 28px; border-radius: 99px; border: 2px solid #e0e0e0;
  background: #fff; color: #555; font-size: 15px; font-weight: 700;
  cursor: pointer; font-family: ${FONT}; transition: all 0.2s;
  &:hover { border-color: ${PRIMARY}; color: ${PRIMARY}; }
`;
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
`;
const Modal = styled.div`
  background: #fff; border-radius: 28px; padding: 28px 24px 24px;
  text-align: center; max-width: 300px; width: 88%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  animation: ${fadeInScale} 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
  font-family: ${FONT};
`;
const ModalEmoji = styled.div`font-size: 52px; margin-bottom: 10px;`;
const ModalTitle = styled.div`font-size: 24px; font-weight: 900; color: #1a2a2a; margin-bottom: 6px;`;
const ModalDesc = styled.div`font-size: 14px; color: #7a8a8a; margin-bottom: 20px; line-height: 1.5;`;
const ModalBtnRow = styled.div`display: flex; gap: 8px; justify-content: center;`;
const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 11px 22px; border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? PRIMARY_GR : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer; font-family: ${FONT};
  box-shadow: ${p => p.$primary ? '0 4px 16px rgba(43,188,175,0.4)' : 'none'};
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); }
`;

function speak(name: string) {
  if (!('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(name);
  u.lang = 'id-ID'; u.rate = 0.8;
  const v = speechSynthesis.getVoices().find(x => x.lang.startsWith('id'));
  if (v) u.voice = v;
  speechSynthesis.speak(u);
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MemoryGame() {
  const navigate = useNavigate();
  const { type } = useParams<{ type?: string }>();
  const [showIntro, setShowIntro] = useState(true);
  const [gameMode, setGameMode]   = useState<GameMode>(type === 'alphabet' ? 'alphabets' : 'animals');
  const [cards, setCards]         = useState<Card[]>([]);
  const [flipped, setFlipped]     = useState<number[]>([]);
  const [matched, setMatched]     = useState<number[]>([]);
  const [moves, setMoves]         = useState(0);
  const [gameWon, setGameWon]     = useState(false);
  const [showModal, setShowModal] = useState(false);

  const initGame = useCallback((mode: GameMode) => {
    let data: MemItem[];
    if (mode === 'animals') {
      data = ANIMALS;
    } else {
      data = [...ALPHABETS].sort(() => Math.random() - 0.5).slice(0, 8);
    }
    const c = [...data, ...data].sort(() => Math.random() - 0.5)
      .map((item, i) => ({ id: i, symbol: item.symbol, isFlipped: false, isMatched: false }));
    setCards(c); setFlipped([]); setMatched([]); setMoves(0); setGameWon(false); setShowModal(false);
  }, []);

  useEffect(() => { if (!showIntro) initGame(gameMode); }, [showIntro, gameMode]);

  const handleCardClick = (id: number) => {
    if (flipped.length === 2 || flipped.includes(id) || matched.includes(id)) return;
    const card = cards[id];
    const data = gameMode === 'animals' ? ANIMALS : ALPHABETS;
    const item = data.find(a => a.symbol === card.symbol);
    if (item) speak(item.name);

    const newFlipped = [...flipped, id];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = newFlipped;
      if (cards[a].symbol === cards[b].symbol) {
        playCorrect();
        const newMatched = [...matched, a, b];
        setMatched(newMatched); setFlipped([]);
        if (newMatched.length === cards.length) {
          setGameWon(true);
          setTimeout(() => {
            setShowModal(true);
            playClap();
          }, 400);
        }
      } else {
        playWrong();
        setTimeout(() => setFlipped([]), 1000);
      }
    }
  };

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
        <TopNav><BackBtn onClick={() => navigate('/')}><span className="material-symbols-outlined">arrow_back</span></BackBtn></TopNav>
        <CharArea>
          <FloatCard $top="16%" $left="8%" $delay={0}>
            <img src={`/assets/images/animals/animal_dog.png?${IMG_VERSION}`} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          </FloatCard>
          <FloatCard $top="12%" $right="10%" $delay={1}>
            <img src={`/assets/images/animals/animal_cat.png?${IMG_VERSION}`} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          </FloatCard>
          <FloatCard $bottom="22%" $left="10%" $delay={2}>A</FloatCard>
          <FloatCard $bottom="18%" $right="8%" $delay={3}>B</FloatCard>
          <CharCard>🧠</CharCard>
        </CharArea>
        <InfoCard>
          <Handle />
          <IntroTitle>Game <span>Memori!</span></IntroTitle>
          <IntroDesc>Temukan semua pasangan kartu yang cocok. Latih ingatanmu dengan hewan atau huruf alfabet!</IntroDesc>
          <ModeRow>
            <ModeBtn $active={gameMode === 'animals'} onClick={() => setGameMode('animals')}>🐾 Hewan</ModeBtn>
            <ModeBtn $active={gameMode === 'alphabets'} onClick={() => setGameMode('alphabets')}>🔤 Huruf</ModeBtn>
          </ModeRow>
          <StartBtn onClick={() => setShowIntro(false)}>Mulai Bermain</StartBtn>
        </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
          <GameTitle>Game Memori</GameTitle>
        </NavLeft>
        <MovesBadge>Langkah: {moves}</MovesBadge>
      </GameNav>
      <GameBody>
        <CardGrid>
          {cards.map(card => {
            const isFlipped = flipped.includes(card.id);
            const isMatched = matched.includes(card.id);
            const show = isFlipped || isMatched;
            const slug = EMOJI_TO_SLUG[card.symbol];
            return (
              <MemCard key={card.id} $flipped={isFlipped} $matched={isMatched}
                disabled={isMatched} onClick={() => handleCardClick(card.id)}>
                {show
                  ? (slug
                      ? <img src={`/assets/images/animals/animal_${slug}.png?${IMG_VERSION}`} alt={card.symbol} style={{ width: '65%', height: '65%', objectFit: 'contain' }} />
                      : <CardFace $isQuestion={false}>{card.symbol}</CardFace>)
                  : <CardFace $isQuestion>?</CardFace>}
              </MemCard>
            );
          })}
        </CardGrid>
        <NewGameBtn onClick={() => initGame(gameMode)}>🔀 Permainan Baru</NewGameBtn>
      </GameBody>

      {showModal && (
        <ModalOverlay>
          <Modal>
            <ModalEmoji>🎉</ModalEmoji>
            <ModalTitle>Kamu Menang!</ModalTitle>
            <ModalDesc>Semua pasangan ditemukan dalam <strong>{moves} langkah</strong>!</ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => navigate("/games")}>Menu</ModalBtn>
              <ModalBtn $primary onClick={() => initGame(gameMode)}>Main Lagi</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}
    </GameWrap>
  );
}
